import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import { formatMinor } from '@/lib/plan-catalog';
import { formatDate } from '@/lib/utils';
import {
  blockerSchema,
  changeResultSchema,
  checkoutResultSchema,
  usePreviewChange,
  useRefreshSessionBilling,
  type Blocker,
  type ChargeView,
  type Selection,
} from './billing-api';

/**
 * אישור בחירת תוכנית. הסכומים תמיד מהשרת (GET /billing/preview-change) — לא
 * מחושבים כאן. checkout: ניסיון / פג / בוטל / מתנה; change: מנוי פעיל.
 */
export function PlanChangeDialog({
  mode,
  selection,
  onClose,
}: {
  mode: 'checkout' | 'change';
  selection: Selection;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['billing', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const refreshSession = useRefreshSessionBilling();
  const preview = usePreviewChange(selection);
  const [paymentError, setPaymentError] = useState(false);
  const [serverBlockers, setServerBlockers] = useState<Blocker[] | null>(null);
  const money = (minor: number) => formatMinor(minor, i18n.language);

  const planName = t(`billing:plans.${selection.plan}.name`);
  const intervalName = t(`billing:interval.${selection.interval}`);

  const confirm = useMutation({
    mutationFn: async () => {
      const body = {
        plan: selection.plan,
        interval: selection.interval,
        extraSeats: selection.extraSeats,
      };
      if (mode === 'checkout') {
        const r = await request('/billing/checkout', {
          method: 'POST',
          body,
          schema: checkoutResultSchema,
          idempotencyKey: crypto.randomUUID(),
        });
        return { type: 'checkout' as const, r };
      }
      const r = await request('/billing/change', {
        method: 'POST',
        body,
        schema: changeResultSchema,
        idempotencyKey: crypto.randomUUID(),
      });
      return { type: 'change' as const, r };
    },
    onSuccess: async (res) => {
      if (res.type === 'checkout' && res.r.status === 'redirect') {
        window.location.assign(res.r.redirectUrl);
        return;
      }
      if (res.type === 'checkout') toast.success(t('billing:dialog.activated'));
      else if (res.r.kind === 'upgrade') toast.success(t('billing:dialog.upgraded'));
      else if (res.r.kind === 'downgrade')
        toast.success(
          t('billing:dialog.downgradeScheduled', { date: formatDate(res.r.effectiveAt ?? null) }),
        );
      else toast.success(t('billing:current.pendingCleared'));
      await qc.invalidateQueries({ queryKey: ['billing'] });
      await refreshSession();
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'PAYMENT_FAILED') {
        setPaymentError(true);
        toast.error(t('billing:dialog.paymentFailed'), t('billing:dialog.paymentFailedBody'));
        void qc.invalidateQueries({ queryKey: ['billing'] });
        void refreshSession();
        return;
      }
      if (e instanceof ApiError && e.code === 'DOWNGRADE_BLOCKED') {
        const parsed = z.object({ blockers: z.array(blockerSchema) }).safeParse(e.details ?? {});
        setServerBlockers(parsed.success ? parsed.data.blockers : []);
        toast.error(t('billing:dialog.blocked'), e.message);
        return;
      }
      toast.error(t('billing:panel.actionFailed'), e instanceof ApiError ? e.message : undefined);
    },
  });

  const p = preview.data;
  const blockers = serverBlockers ?? p?.blockers ?? [];
  const kind = mode === 'checkout' ? 'checkout' : (p?.kind ?? 'upgrade');
  const title =
    kind === 'checkout'
      ? t('billing:dialog.titleCheckout', { plan: planName })
      : kind === 'downgrade'
        ? t('billing:dialog.titleDowngrade', { plan: planName })
        : kind === 'none'
          ? t('billing:dialog.titleNone')
          : t('billing:dialog.titleUpgrade', { plan: planName });
  const confirmLabel =
    kind === 'checkout'
      ? t('billing:dialog.confirmCheckout')
      : kind === 'downgrade'
        ? t('billing:dialog.confirmDowngrade')
        : kind === 'none'
          ? t('billing:dialog.confirmNone')
          : t('billing:dialog.confirmUpgrade');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="plan-change-desc">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription id="plan-change-desc">
            {t('billing:dialog.selection', { plan: planName, interval: intervalName })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {preview.isLoading ? (
            <div className="space-y-2" aria-label={t('billing:dialog.loading')}>
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : preview.isError ? (
            <p role="alert" className="text-sm text-danger">
              {t('billing:dialog.previewFailed')}
            </p>
          ) : p ? (
            <>
              {blockers.length > 0 ? (
                <BlockerList blockers={blockers} />
              ) : kind === 'checkout' ? (
                <>
                  <p className="text-sm text-fg">{t('billing:dialog.checkoutBody')}</p>
                  <Amounts
                    title={t('billing:dialog.firstCharge')}
                    charge={p.newPeriod}
                    money={money}
                  />
                </>
              ) : kind === 'upgrade' ? (
                <>
                  <p className="text-sm text-fg">
                    {t('billing:dialog.upgradeBody', { date: formatDate(p.periodEnd) })}
                  </p>
                  <Amounts title={t('billing:dialog.dueNow')} charge={p.due} money={money} />
                  {p.creditMinor > 0 ? (
                    <p className="text-xs text-fg-muted">
                      {t('billing:dialog.credit', { amount: money(p.creditMinor) })}
                    </p>
                  ) : null}
                  <p className="text-xs text-fg-muted">
                    {t(
                      p.newPeriod.vatMinor > 0
                        ? 'billing:dialog.renewal'
                        : 'billing:dialog.renewalNoVat',
                      {
                        amount: money(p.newPeriod.amountBeforeVatMinor),
                        interval: intervalName,
                      },
                    )}
                  </p>
                </>
              ) : kind === 'downgrade' ? (
                <>
                  <p className="text-sm text-fg">
                    {t('billing:dialog.downgradeBody', { date: formatDate(p.effectiveAt) })}
                  </p>
                  <p className="text-xs text-fg-muted">
                    {t(
                      p.newPeriod.vatMinor > 0
                        ? 'billing:dialog.renewal'
                        : 'billing:dialog.renewalNoVat',
                      {
                        amount: money(p.newPeriod.amountBeforeVatMinor),
                        interval: intervalName,
                      },
                    )}
                  </p>
                </>
              ) : (
                <p className="text-sm text-fg">{t('billing:dialog.noneBody')}</p>
              )}
              {paymentError ? (
                <p
                  role="alert"
                  className="rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-xs text-danger"
                >
                  {t('billing:dialog.paymentFailedBody')}
                </p>
              ) : null}
            </>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              {t('common:actions.cancel')}
            </Button>
          </DialogClose>
          <Button
            loading={confirm.isPending}
            disabled={!p || blockers.length > 0}
            onClick={() => {
              setPaymentError(false);
              confirm.mutate();
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Amounts({
  title,
  charge,
  money,
}: {
  title: string;
  charge: ChargeView;
  money: (minor: number) => string;
}) {
  const { t } = useTranslation('billing');
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface-sunken/50 p-3">
      <p className="mb-2 text-xs font-semibold text-fg">{title}</p>
      <dl className="space-y-1 text-xs">
        {charge.discountMinor > 0 ? (
          <div className="flex justify-between text-success">
            <dt>{t('dialog.discount')}</dt>
            <dd className="tabular-nums">−{money(charge.discountMinor)}</dd>
          </div>
        ) : null}
        <div className="flex justify-between text-fg-muted">
          <dt>{t('dialog.beforeVat')}</dt>
          <dd className="tabular-nums">{money(charge.amountBeforeVatMinor)}</dd>
        </div>
        <div className="flex justify-between text-fg-muted">
          <dt>{t('dialog.vat')}</dt>
          <dd className="tabular-nums">{money(charge.vatMinor)}</dd>
        </div>
        <div className="flex justify-between border-t border-border pt-1 text-sm font-semibold text-fg">
          <dt>{t('dialog.total')}</dt>
          <dd className="tabular-nums" data-testid="amount-total">
            {money(charge.totalMinor)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function BlockerList({ blockers }: { blockers: Blocker[] }) {
  const { t } = useTranslation('billing');
  return (
    <div
      role="alert"
      className="space-y-2 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-xs text-fg"
    >
      <p className="flex items-center gap-2 font-semibold text-warning">
        <AlertTriangle className="size-4" aria-hidden />
        {t('dialog.blockersTitle')}
      </p>
      {blockers.map((b, i) => (
        <div key={i} className="space-y-1">
          <p>
            {t('dialog.blockersBody', { used: b.seatsUsed, limit: b.seatLimit, excess: b.excess })}
          </p>
          <ul className="list-disc space-y-0.5 ps-5">
            {b.breakdown
              .filter((r) => r.count > 0)
              .map((r) => (
                <li key={r.role}>
                  {t('dialog.blockerRole', {
                    role: t(`roles.${r.role}`),
                    count: r.count,
                    seats: r.seats,
                  })}
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
